import type { QueryList, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChildren,
} from '@angular/core';

import type { CurrentUser, User, UserBase } from '@recruitee/account-types';
import type { Attachment } from '@recruitee/attachments-types';
import type { Note, NoteId } from '@recruitee/notes-types';

import { NoteItemComponent } from '../note-item/note-item.component';
import { NoteTypingIndicatorMessageComponent } from '../note-typing-indicator-message/note-typing-indicator-message.component';

@Component({
  selector: 'rt-notes-list',
  templateUrl: './notes-list.component.html',
  styleUrls: ['./notes-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NoteTypingIndicatorMessageComponent, NoteItemComponent],
})
export class NotesListComponent {
  @Input() public notes: Note[];
  @Input() public currentAdmin?: CurrentUser;
  @Input()
  public currentlyTypedNotes: Array<{
    admin: UserBase;
    replyToId?: number | null;
  }> = [];

  @Input() public canDeleteAttachments: boolean = false;
  @Input() public canHighLevelDataAccess: boolean = false;
  @Input() public isVisibilityEnabled: boolean = false;
  @Input() public isReadOnly: boolean = false;

  @Input() public notesBeingSaved: NoteId[] = [];
  @Input() public notesBeingDeleted: NoteId[] = [];
  @Input() public notesBeingEdited: NoteId[] = [];
  @Input() public notesBeingRepliedTo: NoteId[] = [];

  @Input() public noteAttachmentsBeingUploaded: { [key: string]: string[] } = {};
  @Input() public noteAttachmentLoading: boolean = false; // TODO: remove
  @Input() public noteAttachmentsTemp: Record<
    string,
    Array<{ uuid: string; attachment: Attachment }>
  >;

  @Input() public adminsList: User[] = [];
  @Input() public disallowedAdminsList: User[] = [];
  @Input() public noteId: number;
  @Input() public withNewStyling: boolean = false;

  @Output() public replyTypingStart: EventEmitter<{ replyToId: NoteId }> = new EventEmitter();
  @Output() public replyTypingFinish: EventEmitter<void> = new EventEmitter();

  @Output() public noteDiscardClick: EventEmitter<Partial<Note>> = new EventEmitter();
  @Output() public noteEditClick: EventEmitter<Partial<Note>> = new EventEmitter();
  @Output() public noteReplyClick: EventEmitter<Partial<Note>> = new EventEmitter();
  @Output() public noteSaveClick: EventEmitter<Partial<Note>> = new EventEmitter();
  @Output() public noteChecklistClick: EventEmitter<{ id: number; bodyJson: object }> =
    new EventEmitter();
  @Output() public noteDeleteClick: EventEmitter<NoteId> = new EventEmitter();
  @Output() public notePinClick: EventEmitter<NoteId> = new EventEmitter();
  @Output() public noteUnpinClick: EventEmitter<NoteId> = new EventEmitter();
  @Output()
  public uploadAttachmentClick: EventEmitter<{
    attachment: { file: File };
    note: Note;
  }> = new EventEmitter();
  @Output()
  public deleteAttachmentClick: EventEmitter<{
    attachmentId: number;
    note?: Note;
  }> = new EventEmitter();

  public cachedReply: { replyToId: number; value: string } | null = null;
  public sortedNotes: Note[];
  public allNotesFlat: Note[];

  @ViewChildren('noteRef', { read: ElementRef })
  private noteItems: QueryList<ElementRef>;

  private sortFunctions = {
    sortReplies: (a: Note, b: Note) => (a.createdAt > b.createdAt ? 1 : -1),
    sortMainNotes: (a: Note, b: Note) => {
      if (a.pinnedAt && !b.pinnedAt) return -1;
      if (!a.pinnedAt && b.pinnedAt) return 1;
      if (a.pinnedAt && b.pinnedAt) return a.pinnedAt > b.pinnedAt ? 1 : -1;

      return a.createdAt > b.createdAt ? -1 : 1;
    },
  };

  public ngAfterViewInit(): void {
    const noteScrollToElement = this.noteItems.find(
      noteItem => noteItem && parseInt(noteItem.nativeElement.dataset.id, 10) === this.noteId,
    );
    if (noteScrollToElement) {
      noteScrollToElement.nativeElement.scrollIntoView();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.notes) {
      this.sortedNotes = this.notes
        .map(note => ({
          ...note,
          replies: [...note.replies].sort(this.sortFunctions.sortReplies),
        }))
        .sort(this.sortFunctions.sortMainNotes);

      this.allNotesFlat = this.flattenNotes(this.sortedNotes);
    }
  }

  public setCachedReply({ replyToId, value }: { replyToId: number; value: string }): void {
    if (value === null) {
      this.cachedReply = null;
    } else {
      this.cachedReply = { replyToId, value };
    }
  }

  public trackByNote(index: number, note: Note): string | number {
    return note?.id || JSON.stringify(note);
  }

  public get typingAdmins(): UserBase[] {
    return this.currentlyTypedNotes.filter(note => !note.replyToId).map(note => note.admin);
  }

  private flattenNotes(notes: Note[]): Note[] {
    if (!notes || !notes.length) return [];

    return notes.flatMap(note => [{ ...note, replies: [] }, ...this.flattenNotes(note.replies)]);
  }
}
